import {omit} from 'lodash-es';

export const convertArrayToTwoLevelObject = (
    array: Record<string, any>[],
    firstLevelKey: string,
    secondLevelKey: string,
) => array.reduce((acc: Record<string, any>, currentItem: Record<string, any>) => {
    const firstLevelName = currentItem[firstLevelKey];
    const secondLevelName = currentItem[secondLevelKey];

    acc[firstLevelName] = {
        ...(acc[firstLevelName] || {}),
        [secondLevelName]: currentItem,
    };

    return acc;
}, {});

export const replaceFirstElementInArray = <T = any> (
    predicate: (value: T, index: number, obj: any[]) => unknown,
    array: T[],
    replacement: T,
) => {
    const firstIndex = array.findIndex(predicate);
    if (firstIndex === -1) return array;
    const before = array.slice(0, firstIndex);
    const after = array.slice(firstIndex + 1, array.length);
    return [...before, replacement, ...after];
};

interface IFlattenDeepArrayByFieldArgs<T extends object> {
    array: T[] | undefined | null;
    fieldName: keyof T;
    shouldOmit?: boolean;
}

export const flattenDeepArrayByField = <T extends object, O extends (keyof T | '') = ''>({
    array,
    fieldName,
    shouldOmit,
}: IFlattenDeepArrayByFieldArgs<T>) => {
    type TArrayItem = O extends '' ? T : Omit<T, O>;

    if (!array) return array;

    const newArray: TArrayItem[] = [];

    const push = (item: TArrayItem) => {
        if (shouldOmit) newArray.push(omit(item, fieldName) as TArrayItem);
        else newArray.push(item);
    };

    array.forEach(item => {
        push(item as TArrayItem);

        const childrenArray = item[fieldName] as any as T[];
        if (childrenArray && Array.isArray(item[fieldName])) {
            const childrenItems = flattenDeepArrayByField<T>({
                array: childrenArray,
                fieldName,
            });
            childrenItems?.forEach(childItem => push(childItem as TArrayItem));
        }
    });
    return newArray;
};

export const areArraysLengthEqual = <T extends any>(first: T[] | undefined, second: T[] | undefined): boolean => {
    const firstArrayLength = first?.length ?? 0;
    const secondArrayLength = second?.length ?? 0;

    return firstArrayLength === secondArrayLength;
};

interface IHasCommonKeyArgs<T> {
    source: T[];
    search: T[];
}

export const hasCommonKey = <T>({source, search}: IHasCommonKeyArgs<T>) => (
    source.some(key => search.includes(key))
);

export const areArraysEqual = <T extends any>(first: T[] | undefined, second: T[] | undefined): boolean => {
    if (first?.length !== second?.length) return false;
    return JSON.stringify(first) === JSON.stringify(second);
}

/**
 * Сравнивает два массива объектов и возвращает массив измененных полей
 *
 * @template T - Тип объектов в массивах
 * @param oldArray - Исходный массив объектов
 * @param newArray - Новый массив объектов для сравнения
 * @param compareByKey - Массив ключей для идентификации соответствующих объектов в массивах (по умолчанию ['id'])
 * @returns Массив объектов, содержащих только ключи сравнения и измененные поля
 *
 * @remarks
 * Функция использует JSON.stringify для сравнения значений, что может иметь ограничения
 * при работе со сложными объектами, датами.
 */
export const getChangedFields = <T extends Record<string, any>>(
    oldArray: T[],
    newArray: T[],
    compareByKey: (keyof T)[] = ['id'] // поля для идентификации объектов (по умолчанию 'id')
): Partial<T>[] => (
    newArray.filter(item => item !== null).map(newItem => {
        // Находим соответствующий объект в старом массиве по указанным полям
        const oldItem = oldArray.find(oldArrayItem =>
            compareByKey.every(key => oldArrayItem[key] === newItem[key])
        );

        if (!oldItem) return null;

        const changes: Partial<T> = {};
        let hasChanges = false;

        // Копируем поля для идентификации
        compareByKey.forEach(key => {
            changes[key] = newItem[key];
        });

        // Проверяем все остальные поля на изменения
        (Object.keys(newItem) as (keyof T)[]).forEach(key => {
            if (!compareByKey.includes(key) && JSON.stringify(oldItem[key]) !== JSON.stringify(newItem[key])) {
                changes[key] = newItem[key];
                hasChanges = true;
            }
        });

        return hasChanges ? newItem : null;
    }).filter(Boolean) as Partial<T>[]
);


/**
 * Преобразует массив объектов, заменяя все пустые строки (`''`), `null` и `undefined` на `null`.
 *
 * @remarks
 * - Функция работает только на верхнем уровне свойств объекта.
 * - Вложенные объекты, массивы и другие структуры не обрабатываются рекурсивно.
 *
 * @param arr - Массив произвольных значений. Если передан не массив, функция вернёт его без изменений.
 * @param ignoreKeys - Массив строковых ключей, которые следует оставить без замены (по умолчанию []).
 * @returns Новый массив, в котором каждый объект обработан: все свойства (кроме указанных в `ignoreKeys`)
 *          со значениями `''`, `null` или `undefined` заменены на `null`.
 */
export const replaceEmptyWithNull = (arr: any[] = [], ignoreKeys: string[] = []) => {
    if (!Array.isArray(arr)) return arr;

    return arr.map(item => {
        if (typeof item !== 'object' || item === null) return item;

        const cleaned: Record<string, any> = {};
        for (const key in item) {
            if (Object.prototype.hasOwnProperty.call(item, key)) {
                const val = item[key];
                // Если ключ в списке игнорируемых — оставляем без изменений
                if (ignoreKeys.includes(key)) {
                    cleaned[key] = val;
                } else {
                    cleaned[key] = (val === '' || val === null || val === undefined) ? null : val;
                }
            }
        }
        return cleaned;
    });
};
